// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Dialog,
  EmptyState,
  KycStatusBadge,
  Skeleton,
  Textarea,
} from "@chotca/ui";
import {
  useAdminWorkerKyc,
  useApproveWorkerKyc,
  useRejectWorkerKyc,
} from "@chotca/api-client";

const rejectSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, "Cần nhập lý do từ chối")
    .max(500, "Lý do tối đa 500 ký tự"),
});
type RejectValues = z.infer<typeof rejectSchema>;

export function WorkerDetailClient({ id }: { id: string }) {
  const nav = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminWorkerKyc(id);
  const approve = useApproveWorkerKyc();
  const reject = useRejectWorkerKyc();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<RejectValues>({
    resolver: zodResolver(rejectSchema),
    mode: "onTouched",
    defaultValues: { reason: "" },
  });
  const reasonLen = (watch("reason") ?? "").trim().length;

  function openReject() {
    reset({ reason: "" });
    setError(null);
    setRejectOpen(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded-md" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="aspect-[1/1.585] w-full rounded-md" />
          <Skeleton className="aspect-[1/1.585] w-full rounded-md" />
          <Skeleton className="aspect-square w-full rounded-md" />
        </div>
        <Skeleton className="h-40 w-full rounded-md" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <EmptyState
        title="Không tải được hồ sơ"
        action={<Button onClick={() => refetch()}>Thử lại</Button>}
      />
    );
  }

  const status = data.status;
  const isPending = status === "PENDING";

  const handleApprove = async () => {
    setError(null);
    try {
      await approve.mutateAsync(id);
      nav("/kyc/workers");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    }
  };

  const handleReject = handleSubmit(async ({ reason }) => {
    setError(null);
    try {
      await reject.mutateAsync({ id, reason });
      setRejectOpen(false);
      nav("/kyc/workers");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    }
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{data.fullName}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-ink/60">
            <KycStatusBadge status={status} />
            <span>
              · Submitted {new Date(data.submittedAt).toLocaleString("vi-VN")}
            </span>
          </div>
        </div>
      </header>

      <section>
        <h2 className="mb-3 text-sm uppercase text-ink/60">Ảnh xác minh</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <PhotoTile
            label="Mặt trước CCCD"
            url={data.cccdFrontSignedUrl}
            aspect="card"
          />
          <PhotoTile
            label="Mặt sau CCCD"
            url={data.cccdBackSignedUrl}
            aspect="card"
          />
          <PhotoTile
            label="Selfie"
            url={data.selfieSignedUrl}
            aspect="square"
          />
        </div>
        <p className="mt-2 text-xs text-ink/50">
          URL ảnh hết hạn lúc{" "}
          {new Date(data.signedUrlExpiresAt).toLocaleTimeString("vi-VN")}.
        </p>
      </section>

      <section className="rounded-md border border-ink/10 bg-paper p-5">
        <h2 className="mb-4 text-sm uppercase text-ink/60">
          Thông tin khai báo
        </h2>
        <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Họ và tên" value={data.fullName} />
          <Field
            label="Số CCCD"
            value={data.cccdNumber || "(không giải mã được)"}
            mono
          />
          <Field label="Ngày sinh" value={data.dob ?? "—"} />
          <Field label="Phone" value={data.userPhone ?? "—"} mono />
          <Field label="Email" value={data.userEmail ?? "—"} />
          <Field
            label="Reviewed"
            value={
              data.reviewedAt
                ? new Date(data.reviewedAt).toLocaleString("vi-VN")
                : "—"
            }
          />
        </dl>
      </section>

      {data.rejectReason ? (
        <section className="rounded-md border-l-4 border-ember bg-ember/5 p-4">
          <p className="text-xs font-bold uppercase text-ember">
            Lý do từ chối lần trước
          </p>
          <p className="mt-1 text-sm text-ink">{data.rejectReason}</p>
        </section>
      ) : null}

      {error ? (
        <p className="rounded-md bg-ember/10 px-3 py-2 text-sm text-ember">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-end gap-3 border-t border-ink/10 pt-4">
        <Button
          variant="ghost"
          onClick={openReject}
          disabled={!isPending || approve.isPending || reject.isPending}
        >
          Từ chối
        </Button>
        <Button
          variant="secondary"
          onClick={handleApprove}
          disabled={!isPending || approve.isPending || reject.isPending}
          loading={approve.isPending}
        >
          Duyệt
        </Button>
      </footer>

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (!reject.isPending) setRejectOpen(false);
        }}
        title="Lý do từ chối"
        description="Lý do sẽ hiện trong app cho worker khi họ mở lại flow để gửi lại hồ sơ."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setRejectOpen(false)}
              disabled={reject.isPending}
            >
              Huỷ
            </Button>
            <Button
              variant="urgent"
              onClick={handleReject}
              loading={reject.isPending}
            >
              Xác nhận từ chối
            </Button>
          </div>
        }
      >
        <form onSubmit={handleReject} noValidate>
          <Textarea
            rows={4}
            placeholder="VD: Ảnh CCCD bị mờ, không đọc được số."
            maxLength={500}
            invalid={!!errors.reason}
            {...register("reason")}
          />
          {errors.reason ? (
            <p className="mt-1 text-xs text-ember">{errors.reason.message}</p>
          ) : null}
          <p className="mt-1 text-xs text-ink/50">{reasonLen}/500</p>
        </form>
      </Dialog>
    </div>
  );
}

function PhotoTile({
  label,
  url,
  aspect,
}: {
  label: string;
  url: string | null | undefined;
  aspect: "card" | "square";
}) {
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded-md border border-ink/10 bg-mist"
    >
      {url ? (
        <img
          src={url}
          alt={label}
          className={
            aspect === "card"
              ? "aspect-[1/1.585] w-full object-cover"
              : "aspect-square w-full object-cover"
          }
        />
      ) : (
        <div className="flex aspect-square items-center justify-center text-sm text-ink/40">
          (no url)
        </div>
      )}
      <div className="border-t border-ink/10 bg-paper px-3 py-2 text-xs font-medium text-ink/70">
        {label} · mở ảnh đầy đủ
      </div>
    </a>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase text-ink/60">{label}</dt>
      <dd className={mono ? "mt-1 font-mono text-sm" : "mt-1 text-sm"}>
        {value}
      </dd>
    </div>
  );
}
